import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Cloud, Settings, Users, Database, Globe, ShoppingCart, Network, Briefcase, Cpu, ShieldCheck } from 'lucide-react';

const servicesDetail = [
    {
        id: "cloud-computing",
        title: "Cloud Computing",
        icon: <Cloud className="w-8 h-8 text-blue-400" />,
        content: `Cloud computing is the on-demand availability of computer system resources, especially data storage and computing power, without direct active management by the user. The term is generally used to describe data centers available to many users over the Internet. Large clouds, predominant today, often have functions distributed over multiple locations from central servers. If the connection to the user is relatively close, it may be designated an edge server.

        Clouds may be limited to a single organization (enterprise clouds), or be available to many organizations (public cloud). Cloud computing relies on sharing of resources to achieve coherence and economies of scale.

        Advocates of public and hybrid clouds note that cloud computing allows companies to avoid or minimize up-front IT infrastructure costs. Proponents also claim that cloud computing allows enterprises to get their applications up and running faster, with improved manageability and less maintenance, and that it enables IT teams to more rapidly adjust resources to meet fluctuating and unpredictable demand.`,
        bgImage: "https://images.unsplash.com/photo-1544197150-b99a580bb7a8?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "erp",
        title: "ERP",
        icon: <Settings className="w-8 h-8 text-gray-400" />,
        content: `Enterprise resource planning (ERP) refers to a type of software that organizations use to manage day-to-day business activities such as accounting, procurement, project management, risk management and compliance, and supply chain operations. A complete ERP suite also includes enterprise performance management, software that helps plan, budget, predict, and report on an organization's financial results.

        ERP systems tie together a multitude of business processes and enable the flow of data between them. By collecting an organization's shared transactional data from multiple sources, ERP systems eliminate data duplication and provide data integrity with a single source of truth.

        Today, ERP systems are critical for managing thousands of businesses of all sizes and in all industries. To these companies, ERP is as indispensable as the electricity that keeps the lights on.`,
        bgImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "crm",
        title: "CRM",
        icon: <Users className="w-8 h-8 text-blue-500" />,
        content: `Customer relationship management (CRM) is a process in which a business or other organization administers its interactions with customers, typically using data analysis to study large amounts of information.

        CRM systems compile data from a range of different communication channels, including a company's website, telephone, email, live chat, marketing materials and, more recently, social media. They allow businesses to learn more about their target audiences and how to best cater to their needs, thus retaining customers and driving sales growth.

        A CRM approach can be used with past, present or potential customers. The concepts, procedures, and rules that an organization follows when communicating with its customers are also part of CRM.`,
        bgImage: "https://images.unsplash.com/photo-1552581234-26160f608093?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "b2b-applications",
        title: "B2B Applications",
        icon: <Database className="w-8 h-8 text-indigo-400" />,
        content: `B2B applications (business-to-business) are software solutions designed to facilitate transactions and interactions between two or more business entities. These applications streamline complex business processes, improve communication, and enhance collaboration between partners, suppliers, and distributors.

        From supply chain management to procurement portals and partner relationship management tools, B2B applications play a crucial role in modern business ecosystems. They focus on efficiency, scalability, and secure data exchange.

        Our B2B offerings help organizations build stronger partnerships by providing intuitive tools for order tracking, inventory management, and automated reporting, all within a secure and reliable framework.`,
        bgImage: "https://images.unsplash.com/photo-1454165833767-02a6ed8a587d?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "b2c-applications",
        title: "B2C Applications",
        icon: <Globe className="w-8 h-8 text-cyan-400" />,
        content: `B2C applications (business-to-consumer) are designed to provide direct value to end-users. These applications focus on delivering a seamless and engaging user experience, often through mobile apps or web platforms.

        Key features usually include intuitive interfaces, personalized content, secure payment gateways, and real-time notifications. Whether it's a fitness app, a media streaming service, or a personal finance tool, B2C apps are built to attract and retain consumers.

        We specialize in creating high-performance B2C applications that resonate with users. By leveraging the latest technologies and design principles, we ensure your application stands out in a crowded marketplace.`,
        bgImage: "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "e-commerce-solutions",
        title: "E-commerce Solutions",
        icon: <ShoppingCart className="w-8 h-8 text-orange-400" />,
        content: `E-commerce solutions encompass everything a business needs to sell products or services online. This includes the online storefront, product management, shopping carts, payment processing, and order fulfillment.

        Modern e-commerce platforms must be mobile-responsive, SEO-friendly, and capable of handling high volumes of traffic during peak seasons. Integration with social media and marketing tools is also essential for driving growth.

        Our comprehensive e-commerce solutions are tailored to your business goals. From small boutique stores to large-scale marketplaces, we provide the tools you need to succeed in the digital economy.`,
        bgImage: "https://images.unsplash.com/photo-1557821552-17105176677c?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "it-infrastructure-management",
        title: "IT Infrastructure Management",
        icon: <Network className="w-8 h-8 text-blue-300" />,
        content: `IT infrastructure management involves the oversight and administration of an organization's information technology components. This includes hardware, software, networking, facilities, and data centers.

        The primary goal of IT management is to ensure that the infrastructure is reliable, secure, and aligned with business objectives. Proactive monitoring, regular maintenance, and strategic planning are all key components.

        We provide expert IT infrastructure management services to help you optimize your technology stack. By minimizing downtime and maximizing performance, we enable your business to operate at its full potential.`,
        bgImage: "https://images.unsplash.com/photo-1558494949-ef010cbdcc51?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "enterprise-application",
        title: "Enterprise Application",
        icon: <Briefcase className="w-8 h-8 text-green-400" />,
        content: `Enterprise applications are large-scale software systems designed to run in a corporate environment. These applications are often complex, multi-functional, and critical to the organization's operations.

        Common examples include human resource management systems, payroll software, and project management tools. Enterprise applications must be highly secure, scalable, and capable of integrating with other business systems.

        Our enterprise application development services focus on building robust and flexible solutions that address your unique business challenges. We work closely with you to ensure our applications deliver maximum value.`,
        bgImage: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "robotic-process-automation",
        title: "Robotic Process Automation",
        icon: <Cpu className="w-8 h-8 text-purple-400" />,
        content: `Robotic Process Automation (RPA) uses software robots to automate repetitive, rule-based tasks formerly performed by humans. This allows employees to focus on higher-value work that requires creativity and critical thinking.

        RPA can be applied to a wide range of business processes, including data entry, invoice processing, and report generation. It improves accuracy, increases efficiency, and reduces operational costs.

        We help organizations implement RPA solutions that streamline their workflows and drive digital transformation. Our expertise in automation ensures you get the most out of your RPA investment.`,
        bgImage: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=2000"
    },
    {
        id: "blockchain-technology",
        title: "Blockchain Technology",
        icon: <ShieldCheck className="w-8 h-8 text-yellow-500" />,
        content: `Blockchain is a decentralized, distributed ledger technology that records transactions across many computers. This ensures that the record is transparent, secure, and immutable.

        While most famously used for cryptocurrencies, blockchain technology has parity-reaching applications in finance, supply chain management, healthcare, and more. It enables trustless transactions and improves data integrity.

        We offer blockchain development and consulting services to help you explore the potential of this transformative technology. From smart contracts to private blockchain networks, we provide the expertise you need.`,
        bgImage: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&q=80&w=2000"
    }
];

const ServicesDetailPage = () => {
    const { hash } = useLocation();
    const [highlightedId, setHighlightedId] = React.useState<string | null>(null);

    useEffect(() => {
        if (hash) {
            const id = hash.replace('#', '');
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                setHighlightedId(id);
                // Clear highlight after animation
                const timer = setTimeout(() => setHighlightedId(null), 2000);
                return () => clearTimeout(timer);
            }
        } else {
            window.scrollTo(0, 0);
        }
    }, [hash]);

    return (
        <div className="bg-[#0a192f] text-white pt-20">
            {servicesDetail.map((service, index) => (
                <section
                    key={service.id}
                    id={service.id}
                    className={`relative min-h-[60vh] flex items-center py-20 border-b border-white/10 transition-colors duration-500 ${highlightedId === service.id ? 'animate-highlight' : ''
                        }`}
                >
                    {/* Background with overlay */}
                    <div
                        className="absolute inset-0 z-0 bg-cover bg-fixed bg-center"
                        style={{ backgroundImage: `url(${service.bgImage})` }}
                    >
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"></div>
                    </div>

                    <div className="container mx-auto px-6 md:px-12 relative z-10">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-white/10 rounded-lg backdrop-blur-md border border-white/20">
                                {service.icon}
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                                {service.title}
                            </h2>
                        </div>
                        <div className="max-w-4xl">
                            <p className="text-lg md:text-xl text-white/80 leading-relaxed whitespace-pre-line">
                                {service.content}
                            </p>
                        </div>
                    </div>
                </section>
            ))}

            {/* CTA Section at bottom */}
            <section className="bg-white py-16 md:py-20 px-6">
                <div className="container mx-auto max-w-5xl flex flex-col md:flex-row items-center justify-between gap-10 md:gap-8">
                    <div className="text-center md:text-left">
                        <h3 className="text-gray-900 text-2xl md:text-3xl font-light mb-2">Let's Work Together</h3>
                    </div>
                    <div className="text-center md:text-left">
                        <h3 className="text-gray-900 text-2xl md:text-3xl font-bold leading-tight">
                            Our <span className="text-orange-500">Excellency</span> for your<br className="hidden md:block" />
                            <span className="text-gray-900">Business Support</span>
                        </h3>
                    </div>
                    <div className="bg-gray-100 p-4 rounded-lg shadow-inner">
                        <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center">
                            <svg className="w-10 h-10 md:w-12 md:h-12 text-blue-900" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M7 17l9-9M7 8h9v9" />
                            </svg>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicesDetailPage;
